import { type ComponentProps, useLayoutEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { motion, useInView, type Variants } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { type VideoRef } from '@/components/Video/Video'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { ArrowDownIcon } from 'ui/src/icons/ArrowIcon'
import { ImageStory, TextStory, VideoStory } from './Story'
import type { ImageItem, Item, StoryAspectRatio, StoryVariant, TextItem, VideoItem } from './StoryCarousel.types'
import {
  DEFAULT_SCROLL_AMOUNT,
  scrollButton,
  stories,
  storiesContainer,
  storiesWithFadeInOffset,
} from './StoryCarouselDesktop.css'

const STAGGER_DELAY = 0.1

const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: STAGGER_DELAY,
    },
  },
}

// No visual animation here — just an empty variant so Framer Motion can apply stagger timing.
// The actual opacity/y animation lives on StoryWrapper in Story.tsx, keeping backdrop-filter
// out of any parent that creates a compositing surface during the animation.
const itemOrchestratorVariants: Variants = {
  hidden: {},
  visible: {},
}

type StoryCarouselDesktopProps = {
  items: Array<Item>
  aspectRatio?: StoryAspectRatio
  storyVariant?: StoryVariant
  accessibilityLabel?: string
  fadeInAnimation?: boolean
}

export function StoryCarouselDesktop({
  items,
  aspectRatio = '3 / 4',
  storyVariant,
  accessibilityLabel,
  fadeInAnimation,
}: StoryCarouselDesktopProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const firstItemRef = useRef<HTMLDivElement>(null)
  const shouldAnimate = fadeInAnimation === true
  const hasEnteredViewport = useInView(containerRef, { once: true, amount: 0.5 })
  const isFirstItemVisible = useInView(firstItemRef, { root: scrollContainerRef, amount: 'all', initial: true })
  const [isAtScrollEnd, setIsAtScrollEnd] = useState(false)

  // Tracks whether to show the scroll-right button. useLayoutEffect runs synchronously before
  // the first paint, letting us read actual scroll dimensions and set the correct initial state.
  // useInView would return its `initial` value for the first render (before the IntersectionObserver
  // fires), causing the button to flash in/out on load.
  useLayoutEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const checkScrollEnd = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container
      setIsAtScrollEnd(scrollLeft + clientWidth >= scrollWidth - 1)
    }

    const resizeObserver = new ResizeObserver(checkScrollEnd)

    checkScrollEnd()
    container.addEventListener('scroll', checkScrollEnd)
    resizeObserver.observe(container)
    return () => {
      container.removeEventListener('scroll', checkScrollEnd)
      resizeObserver.disconnect()
    }
  }, [])

  const animationState = !shouldAnimate || hasEnteredViewport ? 'visible' : 'hidden'

  if (items.length === 0) return null

  const hasMultipleItems = items.length > 1
  const scrollLeft = () => scrollContainerRef.current?.scrollBy({ left: -DEFAULT_SCROLL_AMOUNT, behavior: 'smooth' })
  const scrollRight = () => scrollContainerRef.current?.scrollBy({ left: DEFAULT_SCROLL_AMOUNT, behavior: 'smooth' })

  return (
    <div ref={containerRef} className={storiesContainer}>
      {hasMultipleItems && !isFirstItemVisible && (
        <ScrollButton direction="left" onClick={scrollLeft} aria-label={t('SCROLL_LEFT')} />
      )}

      <motion.div
        ref={scrollContainerRef}
        className={clsx(stories, shouldAnimate && storiesWithFadeInOffset)}
        role="region"
        tabIndex={0} // Make it a scrollable region for keyboard navigation
        aria-label={accessibilityLabel || t('FEATURED_HIGHLIGHTS')}
        variants={shouldAnimate ? containerVariants : undefined}
        initial={shouldAnimate ? 'hidden' : false}
        animate={shouldAnimate ? animationState : false}
      >
        {items.map((item, index) => (
          <motion.div
            key={item.id}
            ref={index === 0 ? firstItemRef : undefined}
            id={index === 0 ? 'first-item' : index === items.length - 1 ? 'last-item' : undefined}
            variants={shouldAnimate ? itemOrchestratorVariants : undefined}
          >
            <Story item={item} aspectRatio={aspectRatio} storyVariant={storyVariant} shouldFadeIn={shouldAnimate} />
          </motion.div>
        ))}
      </motion.div>

      {hasMultipleItems && !isAtScrollEnd && (
        <ScrollButton direction="right" onClick={scrollRight} aria-label={t('SCROLL_RIGHT')} />
      )}
    </div>
  )
}

function Story({
  item,
  aspectRatio,
  storyVariant,
  shouldFadeIn,
}: {
  item: Item
  aspectRatio: StoryAspectRatio
  storyVariant?: StoryVariant
  shouldFadeIn?: boolean
}) {
  if (!item.asset) {
    return (
      <TextStory
        item={item as TextItem}
        aspectRatio={aspectRatio}
        storyVariant={storyVariant}
        shouldFadeIn={shouldFadeIn}
      />
    )
  }

  switch (item.asset.type) {
    case 'image':
      return (
        <ImageStory
          item={item as ImageItem}
          aspectRatio={aspectRatio}
          storyVariant={storyVariant}
          shouldFadeIn={shouldFadeIn}
        />
      )
    case 'video':
      return (
        <PausableVideoStory
          item={item as VideoItem}
          aspectRatio={aspectRatio}
          storyVariant={storyVariant}
          shouldFadeIn={shouldFadeIn}
        />
      )
    default:
      return null
  }
}

function PausableVideoStory({
  item,
  aspectRatio,
  storyVariant,
  shouldFadeIn,
}: {
  item: VideoItem
  aspectRatio: StoryAspectRatio
  storyVariant?: StoryVariant
  shouldFadeIn?: boolean
}) {
  const videoRef = useRef<VideoRef>(null)

  return (
    <div onMouseEnter={() => videoRef.current?.play()} onMouseLeave={() => videoRef.current?.reset()}>
      <VideoStory
        ref={videoRef}
        item={item}
        aspectRatio={aspectRatio}
        storyVariant={storyVariant}
        shouldFadeIn={shouldFadeIn}
      />
    </div>
  )
}

function ScrollButton({
  direction,
  className,
  ...others
}: ComponentProps<typeof IconButton> & { direction: 'left' | 'right' }) {
  return (
    <IconButton
      className={clsx(scrollButton, className)}
      {...others}
      variant="secondary-alt"
      data-direction={direction}
    >
      <ArrowDownIcon size="1.5rem" />
    </IconButton>
  )
}
